import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Transaction, PromptPaySettings, LINEProfile } from '@tangtung/shared';

type ThemeMode = 'light' | 'dark' | 'system';

interface AppState {
    // Auth
    isReady: boolean;
    user: LINEProfile | null;
    dbUser: User | null;

    // Data
    transactions: Transaction[];
    promptPay: PromptPaySettings;

    // UI
    isOnline: boolean;
    toast: { message: string; visible: boolean };
    themeMode: ThemeMode;

    // Calculator
    calcExpression: string;
    calcResult: number;
    calcHasResult: boolean; // Track if = was pressed

    // Actions
    setReady: (ready: boolean) => void;
    setUser: (user: LINEProfile | null) => void;
    setDbUser: (user: User | null) => void;
    setTransactions: (transactions: Transaction[]) => void;
    addTransaction: (transaction: Transaction) => void;
    setPromptPay: (settings: PromptPaySettings) => void;
    setOnline: (online: boolean) => void;
    showToast: (message: string) => void;
    hideToast: () => void;
    setThemeMode: (mode: ThemeMode) => void;

    // Calculator actions
    setCalcExpression: (expr: string) => void;
    setCalcResult: (result: number, hasResult?: boolean) => void;
    clearCalc: () => void;
}

export const useStore = create<AppState>()(
    persist(
        (set) => ({
            // Initial state
            isReady: false,
            user: null,
            dbUser: null,
            transactions: [],
            promptPay: { number: '', name: '', type: 'phone' },
            isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
            toast: { message: '', visible: false },
            themeMode: 'light',
            calcExpression: '',
            calcResult: 0,
            calcHasResult: false,

            // Actions
            setReady: (ready) => set({ isReady: ready }),
            setUser: (user) => set({ user }),
            setDbUser: (user) => set({ dbUser: user }),
            setTransactions: (transactions) => set({ transactions }),
            addTransaction: (transaction) =>
                set((state) => ({
                    transactions: [transaction, ...state.transactions]
                })),
            setPromptPay: (settings) => set({ promptPay: settings }),
            setOnline: (online) => set({ isOnline: online }),

            showToast: (message) => {
                set({ toast: { message, visible: true } });
                setTimeout(() => {
                    set({ toast: { message: '', visible: false } });
                }, 2500);
            },
            hideToast: () => set({ toast: { message: '', visible: false } }),

            setThemeMode: (mode) => {
                set({ themeMode: mode });
                const root = document.documentElement;
                if (mode === 'dark') {
                    root.classList.add('dark');
                    root.classList.remove('light');
                } else if (mode === 'light') {
                    root.classList.remove('dark');
                    root.classList.add('light');
                } else {
                    root.classList.remove('dark', 'light');
                }
            },

            // Calculator
            setCalcExpression: (expr) => set({ calcExpression: expr, calcHasResult: false }),
            setCalcResult: (result, hasResult = true) => set({ calcResult: result, calcHasResult: hasResult }),
            clearCalc: () => set({ calcExpression: '', calcResult: 0, calcHasResult: false }),
        }),
        {
            name: 'tangtung-storage',
            partialize: (state) => ({
                transactions: state.transactions,
                promptPay: state.promptPay,
                themeMode: state.themeMode,
            }),
        }
    )
);
